import React from 'react';
import { TextBadge } from '@/UIlibrary/TextBadge/TextBadge';
import { COLOR_TYPE } from '@/UIlibrary/globalTypes';
import { MicrocreditRequestStatus } from '@/graphql/__generated__/credit-requests';
import { useTranslations } from 'use-intl';

interface RequestStatusBadgeProps {
	status: MicrocreditRequestStatus;
	size?: 'sm' | 'md' | 'lg';
}

export const RequestStatusBadge: React.FC<RequestStatusBadgeProps> = ({ status, size = 'sm' }) => {
	const t = useTranslations('MicrocreditRequests');

	// Map statuses to badge color types from your UI library
	const statusStyles: Record<MicrocreditRequestStatus, COLOR_TYPE> = {
		CREATED: 'warning',
		PERSONAL_INFORMATION_REGISTERED: 'primary',
		ACTIVITY_INFORMATION_REGISTERED: 'primary',
		CREDIT_DETAILS_REGISTERED: 'primary',
		MONTHLY_BUSINESS_EXPENSES_REGISTERED: 'primary',
		MONTHLY_HOUSE_EXPENSES_REGISTERED: 'primary',
		MONTHLY_INCOME_INFORMATION_REGISTERED: 'primary',
		OTHER_CREDITS_REGISTERED: 'primary',
		VALUE_OF_ASSETS_REGISTERED: 'primary',
		COMPLETED: 'success',
		IN_REVIEW: 'warning',
		REJECTED: 'error',
		PASSED_TO_CREDIT_COMMITTEE: 'warning',
		APPROVED_BY_CREDIT_COMMITTEE: 'success',
		REJECTED_BY_CREDIT_COMMITTEE: 'error'
	};

	// Map statuses to display names using translations
	const statusDisplayNames: Record<MicrocreditRequestStatus, string> = {
		CREATED: t('CreatedStatus'),
		PERSONAL_INFORMATION_REGISTERED: t('PersonalInformationStatus'),
		ACTIVITY_INFORMATION_REGISTERED: t('BusinessInformationStatus'),
		CREDIT_DETAILS_REGISTERED: t('CreditDetailsStatus'),
		MONTHLY_BUSINESS_EXPENSES_REGISTERED: t('BusinessExpensesStatus'),
		MONTHLY_HOUSE_EXPENSES_REGISTERED: t('HouseExpensesStatus'),
		MONTHLY_INCOME_INFORMATION_REGISTERED: t('IncomeInformationStatus'),
		OTHER_CREDITS_REGISTERED: t('OtherCreditsStatus'),
		VALUE_OF_ASSETS_REGISTERED: t('AssetsValueStatus'),
		COMPLETED: t('CompletedStatus'),
		IN_REVIEW: t('InReviewStatus'),
		REJECTED: t('RejectedStatus'),
		PASSED_TO_CREDIT_COMMITTEE: t('PassedToCommitteeStatus'),
		APPROVED_BY_CREDIT_COMMITTEE: t('ApprovedByCommitteeStatus'),
		REJECTED_BY_CREDIT_COMMITTEE: t('RejectedByCommitteeStatus')
	};

	const displayText = `${statusDisplayNames[status] || t('Status')}`;

	return <TextBadge text={displayText} size={size} type={statusStyles[status] || 'gray'} />;
};

// Optional: Progress step indicator component
// You can use this in detailed views to show where in the overall flow a request is
export const RequestStatusProgress: React.FC<{ currentStatus: MicrocreditRequestStatus }> = ({
	currentStatus
}) => {
	const t = useTranslations('MicrocreditRequests');

	// Define the sequence of statuses in the application flow
	const statusFlow = [
		MicrocreditRequestStatus.CREATED,
		MicrocreditRequestStatus.PERSONAL_INFORMATION_REGISTERED,
		MicrocreditRequestStatus.ACTIVITY_INFORMATION_REGISTERED,
		MicrocreditRequestStatus.CREDIT_DETAILS_REGISTERED,
		MicrocreditRequestStatus.MONTHLY_INCOME_INFORMATION_REGISTERED,
		MicrocreditRequestStatus.MONTHLY_BUSINESS_EXPENSES_REGISTERED,
		MicrocreditRequestStatus.MONTHLY_HOUSE_EXPENSES_REGISTERED,
		MicrocreditRequestStatus.VALUE_OF_ASSETS_REGISTERED,
		MicrocreditRequestStatus.OTHER_CREDITS_REGISTERED,
		MicrocreditRequestStatus.COMPLETED
	];

	// Map statuses to display names using translations
	const statusDisplayNames: Record<MicrocreditRequestStatus, string> = {
		CREATED: t('CreatedStatus'),
		PERSONAL_INFORMATION_REGISTERED: t('PersonalInformationStatus'),
		ACTIVITY_INFORMATION_REGISTERED: t('BusinessInformationStatus'),
		CREDIT_DETAILS_REGISTERED: t('CreditDetailsStatus'),
		MONTHLY_BUSINESS_EXPENSES_REGISTERED: t('BusinessExpensesStatus'),
		MONTHLY_HOUSE_EXPENSES_REGISTERED: t('HouseExpensesStatus'),
		MONTHLY_INCOME_INFORMATION_REGISTERED: t('IncomeInformationStatus'),
		OTHER_CREDITS_REGISTERED: t('OtherCreditsStatus'),
		VALUE_OF_ASSETS_REGISTERED: t('AssetsValueStatus'),
		COMPLETED: t('CompletedStatus'),
		IN_REVIEW: t('InReviewStatus'),
		REJECTED: t('RejectedStatus'),
		PASSED_TO_CREDIT_COMMITTEE: t('PassedToCommitteeStatus'),
		APPROVED_BY_CREDIT_COMMITTEE: t('ApprovedByCommitteeStatus'),
		REJECTED_BY_CREDIT_COMMITTEE: t('RejectedByCommitteeStatus')
	};

	const currentIndex = statusFlow.indexOf(currentStatus);
	const totalSteps = statusFlow.length;
	const progress = Math.round(((currentIndex + 1) / totalSteps) * 100);

	return (
		<div className="w-full">
			{/* Progress bar */}
			<div className="w-full bg-gray-200 rounded-full h-2.5 mb-4">
				<div className="bg-blue-600 h-2.5 rounded-full" style={{ width: `${progress}%` }} />
			</div>

			{/* Current status indicator */}
			<div className="flex justify-between items-center">
				<div className="text-sm text-gray-700">{statusDisplayNames[currentStatus]}</div>
				<div className="text-sm font-medium text-gray-700">
					{progress}% {t('Complete')}
				</div>
			</div>
		</div>
	);
};
